import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { customerName } from '@/utils/format';
import { WAIVER_DOCUMENT, ALL_WAIVER_CLAUSE_IDS } from '@/domain/waiverDocument';
import { seedNow } from '@/domain/seedContext';
import type { Booking, Channel, Waiver, WaiverAnswers, WaiverChannel } from '@/domain/types';

function nowIso() {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Days before the session that the waiver is scheduled to go out (I2, 15 Jul).
 * L6 (17 Jul): moved 7 -> 4 (client). Note this is the WAIVER lead only - the
 * 7-day confirmation CALL lane is unchanged, as is the deposit top-up T-7 clock.
 */
const WAIVER_LEAD_DAYS = 4;

/**
 * When a booking's waiver is due to dispatch: exactly `WAIVER_LEAD_DAYS` before
 * the session start (Prompt I2). Pure. The dispatch decision compares this to
 * "now"; a booking that becomes confirmed already inside the window is due
 * immediately, so the send stamps the current sim clock ("clamped to now").
 */
export function waiverDueAt(booking: Booking): string {
  return new Date(new Date(booking.start).getTime() - WAIVER_LEAD_DAYS * 86_400_000).toISOString();
}

/**
 * The date-driven dispatch decision (I2). Sends the waiver only when the booking
 * has entered the T-4d window; otherwise records nothing and leaves it for
 * `reconcileWaiverDispatch` to fire when the sim clock crosses the due instant.
 * Idempotent via `sendWaiver` (an existing waiver of any status short-circuits).
 */
function dispatchWaiverIfDue(bookingId: string): Waiver | undefined {
  const booking = useBrainStore.getState().bookings[bookingId];
  if (!booking) return undefined;
  const existing = latestWaiverForBooking(bookingId);
  if (existing) return existing;
  if (new Date(nowIso()).getTime() < new Date(waiverDueAt(booking)).getTime()) return undefined;
  return sendWaiver({ bookingId });
}

/** The most-recently-created waiver for a booking (waivers are 1-per-booking today). */
export function latestWaiverForBooking(bookingId: string): Waiver | undefined {
  return Object.values(useBrainStore.getState().waivers)
    .filter((w) => w.bookingId === bookingId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

/** The most-recently-created waiver for a customer. */
export function latestWaiverForCustomer(customerId: string): Waiver | undefined {
  return Object.values(useBrainStore.getState().waivers)
    .filter((w) => w.customerId === customerId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

function conversationChannel(channel: WaiverChannel): Channel {
  return channel === 'email' ? 'email' : 'sms';
}

/**
 * Send (or resend) the pre-session medical waiver for a booking. Creates the
 * waiver record if none exists, otherwise flips it back to `sent` and re-stamps
 * `sentAt`. A signed waiver is never re-sent. Mirrors the deposit-link flow:
 * updates the record, drops a lifecycle entry into the customer conversation,
 * appends an activity, and publishes `waiver:sent`.
 */
export function sendWaiver(opts: {
  bookingId: string;
  channel?: WaiverChannel;
  resend?: boolean;
}): Waiver | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[opts.bookingId];
  if (!booking) return undefined;

  const existing = latestWaiverForBooking(opts.bookingId);
  if (existing?.status === 'signed') return existing; // never re-send a signed waiver

  const channel: WaiverChannel = opts.channel ?? existing?.sendChannel ?? 'sms';
  const at = nowIso();
  const isResend = Boolean(existing);

  const waiver: Waiver = existing
    ? { ...existing, status: 'sent', sendChannel: channel, sentAt: at }
    : {
        id: `waiver-${booking.id}`,
        customerId: booking.customerId,
        bookingId: booking.id,
        status: 'sent',
        sendChannel: channel,
        sentAt: at,
        createdAt: at,
      };
  brain.upsertWaiver(waiver);

  const customer = brain.customers[booking.customerId];
  if (customer && channel !== 'manual') {
    const who = customerName(customer);
    brain.addConversationEntry(customer.id, {
      id: `convo-waiver-${waiver.id}-${at}`,
      customerId: customer.id,
      channel: conversationChannel(channel),
      direction: 'out',
      body: isResend
        ? `Resent ${who} the pre-session medical waiver to sign.`
        : `Sent ${who} the pre-session medical waiver to sign.`,
      timestamp: at,
      authorRole: 'agent',
      authorName: 'You',
      waiverId: waiver.id,
    });
  }

  brain.appendActivity({
    id: `act-waiver-sent-${waiver.id}-${at}`,
    type: 'waiver-sent',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: isResend
      ? `Resent waiver to ${booking.customerId}.`
      : `Sent waiver to ${booking.customerId}.`,
    timestamp: at,
  });

  playSound('confirmation-tap');
  eventBus.publish('waiver:sent', { waiverId: waiver.id, bookingId: booking.id });
  return waiver;
}

/**
 * Auto-send hook fired when a booking reaches `confirmed`. Now date-driven (I2,
 * 15 Jul): sends immediately only when the session is inside the T-4d window;
 * further-out bookings record nothing and are picked up by
 * `reconcileWaiverDispatch` when the sim clock crosses the due instant.
 * Idempotent: an existing waiver (any status) short-circuits, so re-confirming /
 * cross-tab replays never double-send or revert a signature.
 */
export function autoSendWaiverOnConfirm(bookingId: string): Waiver | undefined {
  return dispatchWaiverIfDue(bookingId);
}

/**
 * Sim-clock reconciler (I2). Called as the clock advances (alongside
 * `reconcileDepositHolds`): sends the waiver for every live, confirmed,
 * non-superseded booking that has crossed its T-4d due instant and has no waiver
 * yet. Idempotent per booking (an existing waiver short-circuits in
 * `dispatchWaiverIfDue`), so re-crossing the same day is a no-op.
 */
export function reconcileWaiverDispatch(): void {
  const bookings = useBrainStore.getState().bookings;
  for (const booking of Object.values(bookings)) {
    if (booking.status !== 'confirmed') continue;
    dispatchWaiverIfDue(booking.id);
  }
}

/**
 * Carry the waiver across a keep-original reschedule move (I2). One waiver
 * covers the booking chain: a signed/sent waiver is re-pointed onto the new
 * booking (never re-signed, never revoked), then the date-driven dispatch
 * decision runs on the new slot - so a move INTO the window with nothing sent
 * fires the waiver now, while a move further out leaves an already-sent one be.
 */
// ASSUMPTION: waiver is per booking chain, not per piece - pending the client's
// per-piece answer; one signed waiver covers the chain.
export function carryWaiverThroughReschedule(fromBookingId: string, toBookingId: string): void {
  const existing = latestWaiverForBooking(fromBookingId);
  if (existing) {
    useBrainStore.getState().upsertWaiver({ ...existing, bookingId: toBookingId });
  }
  dispatchWaiverIfDue(toBookingId);
}

function mergeUnique(a?: string[], b?: string[]): string[] {
  return Array.from(new Set([...(a ?? []), ...(b ?? [])]));
}

/**
 * Sign a waiver — used both by the customer-facing simulate flow and by a
 * manager signing at the desk. Records the questionnaire answers, stamps
 * `signedAt`, and merges disclosed medical answers into the customer's medical
 * profile so the derived `medical-note` flag reflects them. Idempotent: signing
 * an already-signed waiver is a no-op.
 */
export function signWaiver(opts: {
  waiverId: string;
  answers: WaiverAnswers;
  channel?: WaiverChannel;
  by?: string;
}): Waiver | undefined {
  const brain = useBrainStore.getState();
  const existing = brain.waivers[opts.waiverId];
  if (!existing) return undefined;
  if (existing.status === 'signed') return existing;

  const at = nowIso();
  const signed: Waiver = {
    ...existing,
    status: 'signed',
    sendChannel: opts.channel ?? existing.sendChannel,
    signedAt: at,
    answers: opts.answers,
    documentVersion: WAIVER_DOCUMENT.version,
  };
  brain.upsertWaiver(signed);

  const customer = brain.customers[existing.customerId];

  // Feed disclosed medical detail back into the standing medical profile so the
  // existing derived `medical-note` booking flag + medical intake UI reflect it.
  const hasMedical =
    opts.answers.allergies.length > 0 ||
    opts.answers.conditions.length > 0 ||
    opts.answers.medications.length > 0;
  if (customer && hasMedical) {
    const prev = customer.medicalProfile;
    brain.updateMedicalProfile(
      customer.id,
      {
        allergies: mergeUnique(prev?.allergies, opts.answers.allergies),
        conditions: mergeUnique(prev?.conditions, opts.answers.conditions),
        medications: mergeUnique(prev?.medications, opts.answers.medications),
        notes: prev?.notes,
      },
      opts.by ?? 'Waiver',
    );
  }

  // Write any corrected contact/identity details back to the customer record.
  // Name is left to the record (we keep the typed name as the signature only) to
  // avoid a lossy full-name → first/last split.
  const id = opts.answers.signedIdentity;
  if (customer && id) {
    const next = {
      ...customer,
      email: id.email?.trim() || customer.email,
      phone: id.phone?.trim() || customer.phone,
      dateOfBirth: id.dateOfBirth?.trim() || customer.dateOfBirth,
      address: id.address?.trim() || customer.address,
    };
    brain.upsertCustomer(next);
  }

  brain.appendActivity({
    id: `act-waiver-signed-${signed.id}-${at}`,
    type: 'waiver-signed',
    customerId: signed.customerId,
    studioId: brain.bookings[signed.bookingId]?.studioId,
    body:
      opts.channel === 'manual'
        ? `Waiver signed at the desk for ${signed.customerId}.`
        : `${signed.customerId} signed their waiver.`,
    timestamp: at,
  });

  playSound('booking-confirmed');
  eventBus.publish('waiver:signed', { waiverId: signed.id, bookingId: signed.bookingId });
  return signed;
}

/**
 * Sign a waiver at the front desk. Creates the waiver record if the booking has
 * none yet (e.g. a walk-in signing on paper), then signs it with `manual`
 * channel. The manager fills the questionnaire on the customer's behalf.
 */
export function signWaiverAtDesk(opts: {
  bookingId: string;
  answers: WaiverAnswers;
  by?: string;
}): Waiver | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[opts.bookingId];
  if (!booking) return undefined;

  let waiver = latestWaiverForBooking(opts.bookingId);
  if (!waiver) {
    const at = nowIso();
    waiver = {
      id: `waiver-${booking.id}`,
      customerId: booking.customerId,
      bookingId: booking.id,
      status: 'sent', // created at the desk; signed immediately below
      sendChannel: 'manual',
      createdAt: at,
    };
    brain.upsertWaiver(waiver);
  }
  return signWaiver({ waiverId: waiver.id, answers: opts.answers, channel: 'manual', by: opts.by });
}

// A clean, fully-agreed signature used by the customer-signing simulation:
// no medical disclosures, every clause acknowledged, happy to be photographed.
const SIMULATED_ANSWERS: WaiverAnswers = {
  allergies: [],
  conditions: [],
  medications: [],
  ageConfirmed: true,
  consent: true,
  acknowledgedClauseIds: ALL_WAIVER_CLAUSE_IDS,
  photoConsent: true,
};

/**
 * Simulate the customer signing the waiver on their device (mirrors the
 * simulated Stripe deposit payment). Auto-fills the questionnaire and signs
 * using the waiver's original send channel, with the customer's name as the
 * typed signature.
 */
export function simulateWaiverSigned(
  waiverId: string,
  answersOverride?: Partial<WaiverAnswers>,
): Waiver | undefined {
  const brain = useBrainStore.getState();
  const existing = brain.waivers[waiverId];
  if (!existing) return undefined;
  const customer = brain.customers[existing.customerId];
  const signatureName = customer ? customerName(customer) : 'Customer';
  return signWaiver({
    waiverId,
    answers: { ...SIMULATED_ANSWERS, signatureName, ...answersOverride },
    channel: existing.sendChannel,
  });
}
